import React, { PureComponent } from 'react';
import { MapContext, layer, source, feature } from 'react-openlayers';
import GeometryType from 'ol/geom/GeometryType';
import type Feature from 'ol/Feature';
import type { SimpleGeometry } from 'ol/geom';
import { getCenter } from 'ol/extent';

import { KML_FEATURE_TYPE } from '../../../featureTypes';
import { LIGHT_BLUE, BLACK, WHITE, GREEN, BROWN, RED, YELLOW } from '../../../rgbColors';
import EventBus from '../../../EventBus';
import { MISSION_EVENTS } from '../../../missionEvents';
import { MAP_EVENTS, dispatchSetCenter, dispatchSetExtent } from '../../../mapEvents';
import { getCurrentMission } from '../../../dataModelHelpers';
import { getFeatureType, getZoneKey, getFieldKey, getCollectedFieldKey, getSelectedFeature } from '../helpers/features';
import { filterDraggable } from '../helpers/selectionRules';
import { MAP_LAYERS_FORM_EVENTS } from '../sidebar/map-layers-form-events';
import { MapType } from '../helpers/mapTypes';
import selectionManager from '../../../services/SelectionManager';
import { DefaultKMLFeatureProperties, JobType, KMLFeature, KMLZoneFeatureProperties } from '../../../types/types';
import { Boundary } from '../../../db';
import { GeoDataUtilities } from '@rogoag/shared-ts';

interface ZonesLayerProps {
  /**
   * When false, `SAMPLE_ZONE` and `VISUAL_ZONE` features are hidden.
   * All other zone types (FIELD, COLLECTED_FIELD, SLOW, UNSAFE,
   * GENERIC_ZONE) render regardless. Mirrors KmlLayer's old behaviour.
   */
  showZones: boolean;
  /**
   * Used by `handleMapLayersFormPolySelected` to filter the selected
   * zone through `filterDraggable` (the same rules KmlLayer applies to
   * map clicks). E.g. SAMPLE_ZONE is not draggable in sampling maps.
   */
  jobType: JobType;
  type: MapType;
}

interface ZonesLayerState {
  features: JSX.Element[];
}

const Z_INDEX_ZONE = 0;
const Z_INDEX_FIELD = 1;
const Z_INDEX_COLLECTED_FIELD = 2;

/**
 * ZonesLayer — focused layer rendering the mission's zone polygons:
 * field, collected_field, sample_zone, visual_zone, slow, unsafe,
 * generic_zone (pullin). Split out of KmlLayer.
 *
 * Iterates `mission.getZones()` directly — no `mission.to_feature`
 * detour. Each `zone.to_feature()` returns one or more
 * `KMLZoneFeatureProperties`-typed features which are dispatched to
 * type-specific renderers via `getFeatureType`.
 *
 * Subscribes to:
 *   - MISSION_EVENTS.CREATED_OR_DELETED (mission swap)
 *   - MISSION_EVENTS.BOUNDARY_UPDATED (geometry edits via
 *     update_or_delete_boundary, makeContainedPolygonsHoles,
 *     fixPolygons, createNewPolygon, undo)
 *   - MAP_EVENTS.ZONES_UPDATED (non-geometry changes: rename,
 *     visibility toggle, label toggle, zone delete/cut/merge)
 *   - MAP_EVENTS.SELECTION_CHANGED (zone outline width bumps on select)
 *   - MAP_LAYERS_FORM_EVENTS.POLY_SELECTED (sidebar "click a zone row"
 *     event — selects the matching feature by poly_id and focuses the
 *     map on it)
 *
 * NOT subscribed to MISSION_EVENTS.UPDATED — that event fires from
 * ~50 sites, most of which don't affect zones.
 *
 * `showZones` prop changes trigger a rebuild via `componentDidUpdate`.
 */
export default class ZonesLayer extends PureComponent<ZonesLayerProps, ZonesLayerState> {
  static contextType = MapContext;
  declare context: React.ContextType<typeof MapContext>;

  constructor(props: ZonesLayerProps) {
    super(props);
    this.state = { features: [] };
    this.rebuildAll = this.rebuildAll.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
    this.handleMapLayersFormPolySelected = this.handleMapLayersFormPolySelected.bind(this);
  }

  private static ZONE_FEATURE_TYPES = new Set([
    KML_FEATURE_TYPE.SAMPLE_ZONE,
    KML_FEATURE_TYPE.VISUAL_ZONE,
    KML_FEATURE_TYPE.FIELD,
    KML_FEATURE_TYPE.COLLECTED_FIELD,
    KML_FEATURE_TYPE.SLOW,
    KML_FEATURE_TYPE.UNSAFE,
    KML_FEATURE_TYPE.GENERIC_ZONE,
  ]);

  /** Key of the selected zone feature on the last render (undefined = none). */
  private prevSelectedZoneKey: string | undefined = undefined;

  componentDidMount() {
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.on(MISSION_EVENTS.BOUNDARY_UPDATED, this.rebuildAll);
    EventBus.on(MAP_EVENTS.ZONES_UPDATED, this.rebuildAll);
    EventBus.on(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
    EventBus.on(MAP_LAYERS_FORM_EVENTS.POLY_SELECTED, this.handleMapLayersFormPolySelected, true);
    this.rebuildAll();
  }

  componentDidUpdate(prevProps: ZonesLayerProps) {
    if (prevProps.showZones !== this.props.showZones) {
      this.rebuildAll();
    }
  }

  /** Only rebuild when the selected zone changed (different zone, or zone ↔ non-zone). */
  private onSelectionChanged() {
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const isZone = ZonesLayer.ZONE_FEATURE_TYPES.has(featureType as any);
    const key = isZone && selectedFeature ? getZoneKey(selectedFeature.getProperties()) : undefined;

    if (key !== this.prevSelectedZoneKey) {
      this.rebuildAll();
    }
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.remove(MISSION_EVENTS.BOUNDARY_UPDATED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.ZONES_UPDATED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
    EventBus.remove(MAP_LAYERS_FORM_EVENTS.POLY_SELECTED, this.handleMapLayersFormPolySelected);
  }

  /**
   * Handle the MapLayersForm sidebar "click a zone row" event. Finds
   * the OL feature with the matching poly_id across all vector layers
   * in the map, selects it via the shared SelectionManager, filters
   * through `filterDraggable` for modify eligibility, and re-focuses
   * the map extent on the selected zone.
   *
   * Moved from KmlLayer in Step 10 of the layer split. Only zone
   * features have a `poly_id`, so this belongs with ZonesLayer.
   */
  handleMapLayersFormPolySelected(polyId: number) {
    if (!polyId) {
      return;
    }

    // setSelected() clears+replaces selectedFeatures in one dispatch.
    // Only clear draggable separately (setSelected doesn't touch it).
    selectionManager.clearDraggable();

    const map = this.context?.map;

    if (!map) {
      console.error('map not available');

      return;
    }

    let olFeature: Feature<SimpleGeometry> | undefined;

    map.getLayers().forEach((mapLayer: any) => {
      if (olFeature) {
        return;
      }

      const src = mapLayer?.getSource?.();

      if (!src?.getFeatures) {
        return;
      }

      const match = src.getFeatures().find((f: Feature<SimpleGeometry>) => f.getProperties().poly_id === polyId);

      if (match) {
        olFeature = match;
      }
    });

    if (!olFeature) {
      console.error('Could not find OpenLayers feature for poly_id:', polyId);

      return;
    }

    // Replicate KmlLayer.updateSelectFeatures's draggable filter so the
    // Modify interaction behaves the same as it would for a map click
    // (e.g. SAMPLE_ZONE is not draggable in sampling mode).
    selectionManager.setSelected([olFeature]);
    selectionManager.setDraggable(filterDraggable([olFeature], this.props.type, this.props.jobType));

    // Focus map on the selected feature
    const geometry = olFeature.getGeometry();

    if (geometry) {
      const featureExtent = geometry.getExtent();
      const center = getCenter(featureExtent);
      dispatchSetExtent(featureExtent);
      dispatchSetCenter(center);
    }
  }

  rebuildAll() {
    console.count('ZonesLayer.rebuildAll');
    const mission = getCurrentMission();

    if (!mission) {
      this.setState({ features: [] });

      return;
    }

    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const isZone = ZonesLayer.ZONE_FEATURE_TYPES.has(featureType as any);
    this.prevSelectedZoneKey = isZone && selectedFeature ? getZoneKey(selectedFeature.getProperties()) : undefined;

    const features: JSX.Element[] = [];
    let index = 0;

    for (const zone of mission.getZones()) {
      const zoneFeatures = zone.to_feature();

      for (const kmlFeature of zoneFeatures) {
        // Mirror KmlLayer's old behaviour: stamp index before dispatch
        // so downstream key generation stays stable across rebuilds.
        (kmlFeature.properties as any).index = index++;
        const generated = this.genZoneFeature(kmlFeature);

        if (generated === null) {
          continue;
        }

        if (Array.isArray(generated)) {
          features.push(...generated);
        } else {
          features.push(generated);
        }
      }
    }

    this.setState({ features });
  }

  /**
   * Returns:
   *   - A single JSX element when the zone renders as one feature
   *   - An array of JSX elements for multi-part zones (field + labels)
   *   - `null` when the zone should be skipped (hidden / invisible /
   *     invalid geometry). Rebuild filters nulls out so
   *     state.features accurately reflects what's actually visible.
   */
  private genZoneFeature(kmlFeature: KMLFeature<KMLZoneFeatureProperties>): JSX.Element | JSX.Element[] | null {
    const featureType = getFeatureType(kmlFeature.properties);

    switch (featureType) {
      case KML_FEATURE_TYPE.SAMPLE_ZONE:
        return this.genSampleZone(kmlFeature as unknown as KMLFeature<DefaultKMLFeatureProperties>);
      case KML_FEATURE_TYPE.VISUAL_ZONE:
        return this.genVisualZone(kmlFeature as unknown as KMLFeature<DefaultKMLFeatureProperties>);
      case KML_FEATURE_TYPE.FIELD:
        return this.genFieldFeature(kmlFeature);
      case KML_FEATURE_TYPE.COLLECTED_FIELD:
        return this.genCollectedFieldFeature(kmlFeature);
      case KML_FEATURE_TYPE.SLOW:
        return this.genSlowFeature(kmlFeature as unknown as KMLFeature<DefaultKMLFeatureProperties>);
      case KML_FEATURE_TYPE.UNSAFE:
        return this.genUnsafeFeature(kmlFeature as unknown as KMLFeature<DefaultKMLFeatureProperties>);
      case KML_FEATURE_TYPE.GENERIC_ZONE:
        return this.genGenericFeature(kmlFeature as unknown as KMLFeature<DefaultKMLFeatureProperties>);
      default:
        // Not a zone — shouldn't happen since we iterate getZones().
        return null;
    }
  }

  // -------------------------------------------------------------------
  // Zone renderers — ported from KmlLayer with minimal changes.
  // -------------------------------------------------------------------

  private genSampleZone(kmlFeature: KMLFeature<DefaultKMLFeatureProperties>): JSX.Element | null {
    if (!kmlFeature.properties.visibility || !this.props.showZones) {
      return null;
    }

    const key = getZoneKey(kmlFeature.properties);
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const selected =
      featureType === KML_FEATURE_TYPE.SAMPLE_ZONE &&
      !!selectedFeature &&
      key === getZoneKey(selectedFeature.getProperties());
    const coords = kmlFeature.geometry.coordinates;

    if (!coords) {
      return null;
    }

    return (
      <feature.PolygonReact
        coordinates={coords}
        fillOptions={{ color: kmlFeature.properties.zone_color.concat([0.3]) }}
        strokeOptions={{ color: BLACK, width: selected ? 12 : 1 }}
        // @ts-ignore
        key={`${key}-${kmlFeature.properties.index ?? 0}-${KML_FEATURE_TYPE.SAMPLE_ZONE}`}
        properties={kmlFeature.properties}
        zIndex={Z_INDEX_ZONE}
        hideAtZoom={14}
        textOptions={{
          text: `${kmlFeature.properties.name}`,
          font: '20px Calibri,sans-serif',
          fillOptions: { color: BLACK },
        }}
      />
    );
  }

  private genVisualZone(kmlFeature: KMLFeature<DefaultKMLFeatureProperties>): JSX.Element | null {
    if (!kmlFeature.properties.visibility || !this.props.showZones) {
      return null;
    }

    const key = getZoneKey(kmlFeature.properties);
    const coords = kmlFeature.geometry.coordinates;

    if (!coords) {
      return null;
    }

    return (
      <feature.PolygonReact
        coordinates={coords}
        fillOptions={{ color: kmlFeature.properties.zone_color.concat([0.3]) }}
        strokeOptions={{ color: BLACK, width: 1 }}
        // @ts-ignore
        key={`${key}-${kmlFeature.properties.index ?? 0}-${KML_FEATURE_TYPE.SAMPLE_ZONE}`}
        properties={kmlFeature.properties}
        zIndex={Z_INDEX_ZONE}
        hideAtZoom={14}
        textOptions={{
          text: `${kmlFeature.properties.name}`,
          font: '20px Calibri,sans-serif',
          fillOptions: { color: BLACK },
        }}
      />
    );
  }

  private genFieldFeature(kmlFeature: KMLFeature<KMLZoneFeatureProperties>): JSX.Element[] | null {
    if (!kmlFeature.properties.visibility) {
      return null;
    }

    const key = getFieldKey(kmlFeature.properties);
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const selected =
      featureType === KML_FEATURE_TYPE.FIELD &&
      !!selectedFeature &&
      key === getFieldKey(selectedFeature.getProperties());
    const FieldFeature =
      kmlFeature.geometry.type === GeometryType.MULTI_POLYGON ? feature.MultiPolygonReact : feature.PolygonReact;

    const coords = kmlFeature.geometry.coordinates;

    if (!coords) {
      return null;
    }

    const uniqueKey = `${key}-${KML_FEATURE_TYPE.FIELD}-${kmlFeature.properties.zone_id}`;

    const features: JSX.Element[] = [];
    const labels: JSX.Element[] = kmlFeature.properties.labels_visible
      ? this.generateLabelsForPolygonRings(kmlFeature, uniqueKey, coords, YELLOW, Z_INDEX_FIELD)
      : [];

    features.push(
      <FieldFeature
        coordinates={coords}
        strokeOptions={{ color: YELLOW, width: selected ? 16 : 3 }}
        properties={kmlFeature.properties}
        key={uniqueKey}
        zIndex={Z_INDEX_FIELD}
      />,
    );

    return [...features, ...labels];
  }

  private genCollectedFieldFeature(kmlFeature: KMLFeature<KMLZoneFeatureProperties>): JSX.Element[] | null {
    if (!kmlFeature.properties.visibility) {
      return null;
    }

    const key = getCollectedFieldKey(kmlFeature.properties);
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const selected =
      featureType === KML_FEATURE_TYPE.COLLECTED_FIELD &&
      !!selectedFeature &&
      key === getCollectedFieldKey(selectedFeature.getProperties());

    const uniqueKey = `${key}-${KML_FEATURE_TYPE.COLLECTED_FIELD}`;
    const coords = kmlFeature.geometry.coordinates;

    if (!coords) {
      return null;
    }

    const features: JSX.Element[] = [];
    const labels: JSX.Element[] = kmlFeature.properties.labels_visible
      ? this.generateLabelsForPolygonRings(kmlFeature, uniqueKey, coords, LIGHT_BLUE, Z_INDEX_COLLECTED_FIELD)
      : [];

    const PolygonComponent =
      kmlFeature.geometry.type === GeometryType.MULTI_POLYGON ? feature.MultiPolygonReact : feature.PolygonReact;

    features.push(
      <PolygonComponent
        key={uniqueKey}
        coordinates={coords}
        strokeOptions={{ color: LIGHT_BLUE, width: selected ? 6 : 4 }}
        properties={kmlFeature.properties}
        zIndex={Z_INDEX_COLLECTED_FIELD}
      />,
    );

    return [...features, ...labels];
  }

  private genSlowFeature(kmlFeature: KMLFeature<DefaultKMLFeatureProperties>): JSX.Element | null {
    if (!kmlFeature.properties.visibility) {
      return null;
    }

    const key = getZoneKey(kmlFeature.properties);
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const selected =
      featureType === KML_FEATURE_TYPE.SLOW && !!selectedFeature && key === getZoneKey(selectedFeature.getProperties());

    if (!kmlFeature.geometry.coordinates) {
      return null;
    }

    return (
      <feature.PolygonReact
        coordinates={kmlFeature.geometry.coordinates}
        fillOptions={{ color: BROWN.concat([0.3]) }}
        strokeOptions={{ color: BLACK, width: selected ? 2 : 1 }}
        properties={kmlFeature.properties}
        key={`${key}-${KML_FEATURE_TYPE.SLOW}`}
        zIndex={Z_INDEX_ZONE}
        hideAtZoom={14}
      />
    );
  }

  private genUnsafeFeature(kmlFeature: KMLFeature<DefaultKMLFeatureProperties>): JSX.Element | null {
    if (!kmlFeature.properties.visibility) {
      return null;
    }

    const key = getZoneKey(kmlFeature.properties);
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const selected =
      featureType === KML_FEATURE_TYPE.UNSAFE &&
      !!selectedFeature &&
      key === getZoneKey(selectedFeature.getProperties());

    if (!kmlFeature.geometry.coordinates) {
      return null;
    }

    return (
      <feature.PolygonReact
        coordinates={kmlFeature.geometry.coordinates}
        fillOptions={{ color: RED.concat(0.3) }}
        strokeOptions={{ color: BLACK, width: selected ? 2 : 1 }}
        properties={kmlFeature.properties}
        key={`${key}-${KML_FEATURE_TYPE.UNSAFE}`}
        zIndex={Z_INDEX_ZONE}
        hideAtZoom={14}
      />
    );
  }

  private genGenericFeature(kmlFeature: KMLFeature<DefaultKMLFeatureProperties>): JSX.Element | null {
    if (!kmlFeature.properties.visibility) {
      return null;
    }

    const key = getZoneKey(kmlFeature.properties);
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const selected =
      featureType === KML_FEATURE_TYPE.GENERIC_ZONE &&
      !!selectedFeature &&
      key === getZoneKey(selectedFeature.getProperties());
    const uniqueKey = `${key}-${KML_FEATURE_TYPE.GENERIC_ZONE}`;
    const coords = kmlFeature.geometry.coordinates;

    if (!coords) {
      return null;
    }

    return (
      <feature.PolygonReact
        coordinates={coords}
        fillOptions={{ color: GREEN.concat(0.3) }}
        strokeOptions={{ color: BLACK, width: selected ? 2 : 1 }}
        properties={kmlFeature.properties}
        key={uniqueKey}
        zIndex={Z_INDEX_ZONE}
        hideAtZoom={14}
      />
    );
  }

  // -------------------------------------------------------------------
  // Label generation — ported verbatim from KmlLayer.
  // -------------------------------------------------------------------

  private generateLabelsForPolygonRings(
    kmlFeature: KMLFeature<DefaultKMLFeatureProperties>,
    uniqueKey: string,
    coords: number[][][] | number[][][][],
    color: number[],
    mainFeatureZIndex: number,
  ): JSX.Element[] {
    const labels: JSX.Element[] = [];

    const generateLabelsForPolygon = (
      kmlFeature: KMLFeature<DefaultKMLFeatureProperties>,
      uniqueKey: string,
      polygonCoords: number[][][],
      color: number[],
      mainFeatureZIndex: number,
      polyIndex: number,
    ) => {
      const [outerRing, ...holes] = polygonCoords;
      const polyId = kmlFeature.properties.poly_id || polyIndex + 1;

      const outerCenter = GeoDataUtilities.calculatePolygonCenter(outerRing);
      labels.push(
        <feature.PointReact
          key={`${uniqueKey}-label-outer-${polyIndex}`}
          coordinate={outerCenter}
          textOptions={{
            text: `P${polyId}`,
            font: '24px Calibri,sans-serif',
            fillOptions: { color: WHITE },
            strokeOptions: { color, width: 1 },
          }}
          zIndex={mainFeatureZIndex + 2}
        />,
      );

      holes.forEach((hole, holeIndex) => {
        const holeCenter = GeoDataUtilities.calculatePolygonCenter(hole);
        const holeId = Boundary.holeLabel(polyId, holeIndex);

        labels.push(
          <feature.PointReact
            key={`${uniqueKey}-label-hole-${polyIndex}-${holeIndex}`}
            coordinate={holeCenter}
            textOptions={{
              text: `H${holeId}`,
              font: '14px Calibri,sans-serif',
              fillOptions: { color: WHITE },
              strokeOptions: { color, width: 1 },
            }}
            zIndex={mainFeatureZIndex + 2}
          />,
        );
      });
    };

    if (kmlFeature.geometry.type === GeometryType.POLYGON) {
      generateLabelsForPolygon(kmlFeature, uniqueKey, coords as number[][][], color, mainFeatureZIndex, 0);
    } else if (kmlFeature.geometry.type === GeometryType.MULTI_POLYGON) {
      (coords as number[][][][]).forEach((poly, index) => {
        generateLabelsForPolygon(kmlFeature, uniqueKey, poly, color, mainFeatureZIndex, index);
      });
    }

    return labels;
  }

  render() {
    return (
      // @ts-ignore - layer.Vector children typing
      <layer.VectorImage zIndex={Z_INDEX_ZONE}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>{this.state.features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
